import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { ErrorMessage } from 'formik'
import { Grid, IconButton, InputAdornment, TextField } from '@mui/material'
import { FormError } from '@dhl/error'
import { Calendar } from '@dhl/icons'
import { WeekDay } from '~/helpers'
import type { CountryCode, Timestamp } from '~/model'
import { Dialog } from './Dialog'
import { useFields } from './useFields'

type Props = {
  // data:
  countryCode: CountryCode
  postalCode: string
  // config:
  sm: boolean
  xs: boolean
  label: string
  useEveningTimeWindows: boolean
  excludedWeekDays: Array<WeekDay>
  // fields:
  dateFieldName: string
  timeWindowStartFieldName?: string
  timeWindowEndFieldName?: string
}

export const PickupDateInput = ({
  countryCode,
  postalCode,
  sm,
  xs,
  label,
  useEveningTimeWindows,
  excludedWeekDays,
  dateFieldName,
  timeWindowStartFieldName,
  timeWindowEndFieldName,
}: Props) => {
  const [dialogOpen, setDialogOpen] = useState(false)
  const { t } = useTranslation()
  const enableTimeWindows = !!timeWindowStartFieldName && !!timeWindowEndFieldName
  const { dateValue, timeWindowStartValue, timeWindowEndValue } = useFields(
    dateFieldName,
    timeWindowStartFieldName,
    timeWindowEndFieldName
  )

  const formatDate = (dv: Timestamp) => dayjs(dv).format('D MMMM YYYY')
  const formattedDate = dateValue ? formatDate(dateValue) : ''
  const formattedTimeWindow =
    enableTimeWindows && timeWindowStartValue && timeWindowEndValue
      ? ` ${t('betweenVal1AndVal2', {
          value1: timeWindowStartValue,
          value2: timeWindowEndValue,
        })}`
      : ''

  const formattedValue = `${formattedDate}${formattedTimeWindow}`

  const handleClose = () => {
    setDialogOpen(false)
  }

  const handleOpen = () => {
    setDialogOpen(true)
  }

  return (
    <>
      <Grid size={{ sm: sm ? 12 : 6, xs: xs ? 12 : 6 }}>
        <TextField
          name={`${dateFieldName}Label`}
          data-test="pickup-date-input"
          type="text"
          label={label}
          fullWidth
          required
          slotProps={{
            input: {
              value: formattedValue,
              onChange: () => {},
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleOpen} size="large">
                    <Calendar />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
          onClick={handleOpen}
        />
        <ErrorMessage name={dateFieldName} component={FormError} />
      </Grid>

      {dialogOpen && (
        <Dialog
          dateFieldName={dateFieldName}
          timeWindowStartFieldName={timeWindowStartFieldName}
          timeWindowEndFieldName={timeWindowEndFieldName}
          countryCode={countryCode}
          postalCode={postalCode}
          useEveningTimeWindows={useEveningTimeWindows}
          excludedWeekDays={excludedWeekDays}
          onClose={handleClose}
        />
      )}
    </>
  )
}
